import React, { useState, useEffect, useMemo } from 'react';
import { logger } from '@/utils/logger';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Clock,
  User,
  Users,
  Phone,
  MapPin,
  DollarSign,
  CreditCard,
  Banknote,
  CheckCircle,
  AlertCircle,
  Plus,
  Minus,
  type LucideIcon
} from 'lucide-react';
import { clsx } from 'clsx';
import { TimeSlot, CreateBookingForm, Location, Court } from '@/types';
import { useAuth } from '@/store/authStore';
import QuickCustomerSearch from '@/components/common/QuickCustomerSearch';
import { api } from '@/services/api';
import toast from 'react-hot-toast';
import { formatNZD } from '@/utils/currency';
import { getNzDateString } from '@/utils/nzDate';


type Step = 'customer' | 'timeslot' | 'payment' | 'confirm';

interface CustomerSelection {
  id: number | string;
  name: string;
  phone?: string;
  email?: string;
  member_level?: string;
  total_bookings?: number;
}

const CreateBooking: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [locations, setLocations] = useState<Location[]>([]);
  const [courts, setCourts] = useState<Court[]>([]);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState<string | null>(null);
  
  const [currentStep, setCurrentStep] = useState<Step>('customer');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState<CreateBookingForm>({
    court_time_slot_id: 0,
    date: getNzDateString(),
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    player_count: 2,
    payment_method: 'cash',
    special_requests: '',
  });
  
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlot | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerSelection | null>(null);
  const [useExistingCustomer, setUseExistingCustomer] = useState(true);
  
  // Time slot filtering
  const [selectedDate, setSelectedDate] = useState(formData.date);
  const [selectedLocationId, setSelectedLocationId] = useState<number | undefined>(undefined);
  const [selectedCourtId, setSelectedCourtId] = useState<number | undefined>(undefined);
  const MAX_PLAYERS = 4;

  useEffect(() => {
    const loadLocations = async () => {
      const result = await api.courts.getLocations();
      if (result.error || !result.data) {
        toast.error(result.error || t('errors.generic'));
        return;
      }
      setLocations(result.data);
    };
    loadLocations();
  }, [t]);

  useEffect(() => {
    const loadCourts = async () => {
      const result = await api.courts.getCourts(selectedLocationId);
      if (result.error || !result.data) {
        setCourts([]);
        return;
      }
      setCourts(result.data);
    };
    loadCourts();
  }, [selectedLocationId]);

  useEffect(() => {
    if (!selectedDate) return;
    const loadSlots = async () => {
      setSlotsLoading(true);
      const result = await api.courts.getAvailableSlots({
        date: selectedDate,
        location_id: selectedLocationId,
        duration: 60
      });
      if (result.error || !result.data) {
        logger.error('Failed to fetch time slots:', result.error);
        setSlotsError(t('errors.serverError'));
        setTimeSlots([]);
      } else {
        setSlotsError(null);
        setTimeSlots(result.data);
      }
      setSlotsLoading(false);
    };
    loadSlots();
  }, [selectedDate, selectedLocationId, t]);

  useEffect(() => {
    if (selectedCourtId && !courts.some(court => court.id === selectedCourtId)) {
      setSelectedCourtId(undefined);
    }
  }, [courts, selectedCourtId]);

  useEffect(() => {
    setSelectedTimeSlot(null);
  }, [selectedDate, selectedLocationId]);

  const paymentOptions: Array<{
    key: CreateBookingForm['payment_method'];
    label: string;
    icon: LucideIcon;
    description: string;
  }> = [
    { key: 'cash', label: t('createBooking.cashPayment'), icon: Banknote, description: t('createBooking.cashPaymentDesc') },
    { key: 'card', label: t('createBooking.cardPayment'), icon: CreditCard, description: t('createBooking.cardPaymentDesc') },
    { key: 'transfer', label: t('createBooking.transferPayment'), icon: Phone, description: t('createBooking.transferPaymentDesc') },
  ];
  
  const availableTimeSlots = useMemo(() => {
    return timeSlots.filter(slot => 
      slot.date === selectedDate &&
      slot.is_active !== false &&
      // available_count 代表该时段还能创建多少条预约，不是单条预约的玩家人数容量
      slot.available_count > 0 &&
      (!selectedCourtId || slot.court_id === selectedCourtId)
    );
  }, [timeSlots, selectedDate, selectedCourtId]);

  const steps = [
    { key: 'customer', label: t('createBooking.customerDetails'), icon: User },
    { key: 'timeslot', label: t('createBooking.selectTime'), icon: Clock },
    { key: 'payment', label: t('createBooking.paymentMethod'), icon: CreditCard },
    { key: 'confirm', label: t('createBooking.confirmBooking'), icon: CheckCircle },
  ];

  const handleNext = () => {
    const stepOrder: Step[] = ['customer', 'timeslot', 'payment', 'confirm'];
    const currentIndex = stepOrder.indexOf(currentStep);
    if (currentIndex < stepOrder.length - 1) {
      setCurrentStep(stepOrder[currentIndex + 1]);
    }
  };

  const handleBack = () => {
    const stepOrder: Step[] = ['customer', 'timeslot', 'payment', 'confirm'];
    const currentIndex = stepOrder.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(stepOrder[currentIndex - 1]);
    } else {
      navigate('/bookings');
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    if (!selectedTimeSlot) {
      toast.error(t('createBooking.selectTimeSlot'));
      setIsSubmitting(false);
      return;
    }

    const payload: CreateBookingForm = {
      ...formData,
      court_time_slot_id: selectedTimeSlot.id,
      date: selectedTimeSlot.date,
      customer_name: formData.customer_name.trim(),
      customer_phone: formData.customer_phone?.trim() || undefined,
      customer_email: formData.customer_email?.trim() || undefined,
    };

    const result = await api.bookings.createWalkIn(payload);

    if (result.error) {
      toast.error(result.error);
    } else {
      logger.info('Creating booking:', {
        ...payload,
        staff_id: user?.id,
      });
      toast.success(t('booking.created', { id: result.data?.id }));
      navigate('/bookings');
    }

    setIsSubmitting(false);
  };

  const handleCustomerSelect = (customer: CustomerSelection) => {
    setSelectedCustomer(customer);
    setFormData({
      ...formData,
      customer_name: customer.name,
      customer_phone: customer.phone,
      customer_email: customer.email || '',
    });
  };

  const handleToggleCustomerMode = () => {
    setUseExistingCustomer(!useExistingCustomer);
    setSelectedCustomer(null);
    setFormData({
      ...formData,
      customer_name: '',
      customer_phone: '',
      customer_email: '',
    });
  };

  const calculateTotal = () => {
    return selectedTimeSlot ? selectedTimeSlot.base_price : 0;
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 'customer':
        return formData.customer_name.trim() !== '' &&
          (formData.customer_phone?.trim() || '') !== '' &&
          formData.player_count >= 1 &&
          formData.player_count <= MAX_PLAYERS;
      case 'timeslot':
        return selectedTimeSlot !== null;
      case 'payment':
        return formData.payment_method === 'cash' || formData.payment_method === 'card' || formData.payment_method === 'transfer';
      case 'confirm':
        return true;
      default:
        return false;
    }
  };

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div className="flex items-center gap-4">
        <button
          onClick={handleBack}
          className="p-2 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('createBooking.title')}</h1>
          <p className="text-gray-500 mt-1">{t('createBooking.walkInBooking')}</p>
        </div>
      </div>

      {/* 步骤指示器 */}
      <div className="card">
        <div className="flex items-center justify-between overflow-x-auto pb-2 sm:pb-0">
          {steps.map((step, index) => {
            const isActive = step.key === currentStep;
            const isCompleted = steps.findIndex(s => s.key === currentStep) > index;
            
            return (
              <div key={step.key} className="flex items-center min-w-0">
                <div className="flex flex-col sm:flex-row items-center">
                  <div className={clsx(
                    "w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-xs sm:text-sm font-medium transition-all",
                    isActive 
                      ? "bg-primary-500 text-white" 
                      : isCompleted 
                      ? "bg-success-500 text-white"
                      : "bg-gray-100 text-gray-400"
                  )}>
                    {isCompleted ? (
                      <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5" />
                    ) : (
                      <step.icon className="h-4 w-4 sm:h-5 sm:w-5" />
                    )}
                  </div>
                  
                  <div className="mt-1 sm:mt-0 sm:ml-3">
                    <div className={clsx(
                      "text-xs sm:text-sm font-medium text-center sm:text-left whitespace-nowrap",
                      isActive ? "text-primary-600" : isCompleted ? "text-success-600" : "text-gray-400"
                    )}>
                      {step.label}
                    </div>
                  </div>
                </div>
                
                {index < steps.length - 1 && (
                  <div className={clsx(
                    "w-8 sm:w-16 h-px mx-2 sm:mx-4 transition-colors",
                    isCompleted ? "bg-success-500" : "bg-gray-200"
                  )} />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* 步骤内容 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
        {/* 主要内容区域 */}
        <div className="lg:col-span-2">
          <AnimatePresence mode="wait">
            {/* 步骤1：客户信息 */}
{currentStep === 'customer' && (
              <motion.div
                key="customer"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="card card-comfortable"
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">{t('createBooking.customerDetails')}</h3>
                  
                  {/* 切换模式按钮 */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleToggleCustomerMode}
                      className={clsx(
                        "px-3 py-1.5 text-sm font-medium rounded-lg transition-colors",
                        useExistingCustomer 
                          ? "bg-primary-100 text-primary-700"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      )}
                    >
                      {t('createBooking.searchCustomer')}
                    </button>
                    <button
                      onClick={handleToggleCustomerMode}
                      className={clsx(
                        "px-3 py-1.5 text-sm font-medium rounded-lg transition-colors",
                        !useExistingCustomer 
                          ? "bg-primary-100 text-primary-700"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      )}
                    >
                      {t('createBooking.newCustomer')}
                    </button>
                  </div>
                </div>
                
                <div className="space-y-6">
                  {/* 快速客户搜索模式 */}
                  {useExistingCustomer && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('createBooking.searchExistingCustomer')}
                        </label>
                        <QuickCustomerSearch 
                          onSelectCustomer={handleCustomerSelect}
                          placeholder={t('createBooking.searchPlaceholder')}
                        />
                      </div>
                      
                      {/* 已选择客户信息显示 */}
                      {selectedCustomer && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="p-4 bg-success-50 border border-success-200 rounded-xl"
                        >
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-medium text-success-900">{t('createBooking.customerSelected')}</h4>
                            <button
                              onClick={() => {
                                setSelectedCustomer(null);
                                setFormData({
                                  ...formData,
                                  customer_name: '',
                                  customer_phone: '',
                                  customer_email: '',
                                });
                              }}
                              className="text-success-600 hover:text-success-800 text-sm"
                            >
                              {t('createBooking.changeCustomer')}
                            </button>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                            <div>
                              <span className="text-success-700">{t('createBooking.customerName')}:</span>
                              <span className="font-medium text-success-900">{selectedCustomer.name}</span>
                            </div>
                            <div>
                              <span className="text-success-700">{t('createBooking.customerPhone')}:</span>
                              <span className="font-medium text-success-900">{selectedCustomer.phone}</span>
                            </div>
                            {selectedCustomer.member_level && (
                              <div>
                                <span className="text-success-700">{t('createBooking.memberLevel')}：</span>
                                <span className="font-medium text-success-900">{selectedCustomer.member_level}</span>
                              </div>
                            )}
                            {selectedCustomer.total_bookings !== undefined && (
                              <div>
                                <span className="text-success-700">{t('createBooking.bookingHistory')}：</span>
                                <span className="font-medium text-success-900">{selectedCustomer.total_bookings}{t('createBooking.times')}</span>
                              </div>
                            )}
                            {selectedCustomer.email && (
                              <div className="md:col-span-2">
                                <span className="text-success-700">{t('createBooking.email')}：</span>
                                <span className="font-medium text-success-900">{selectedCustomer.email}</span>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </div>
                  )}
                  
                  {/* 手动输入新客户模式 */}
                  {!useExistingCustomer && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
{t('createBooking.customerNameRequired')} <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={formData.customer_name}
                            onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                            placeholder={t('createBooking.customerNamePlaceholder')}
                            className="input"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
{t('createBooking.contactPhone')} <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="tel"
                            value={formData.customer_phone}
                            onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                            placeholder={t('createBooking.contactPhonePlaceholder')}
                            className="input"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
{t('createBooking.emailOptional')}
                        </label>
                        <input
                          type="email"
                          value={formData.customer_email}
                          onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                          placeholder="customer@example.com"
                          className="input"
                        />
                      </div>
                    </div>
                  )}

                  {/* 参与人数 - 两种模式都需要 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
{t('createBooking.playerCount')}
                    </label>
                    <div className="flex items-center gap-4">
                      <button
                        onClick={() => setFormData({ ...formData, player_count: Math.max(1, formData.player_count - 1) })}
                        className="p-2 rounded-xl border border-gray-300 hover:bg-gray-50 transition-colors"
                      >
                        <Minus className="h-4 w-4" />
                      </button>
                      
                      <div className="flex items-center gap-2 min-w-20 justify-center">
                        <Users className="h-4 w-4 text-gray-400" />
                        <span className="text-lg font-semibold">{formData.player_count}</span>
                      </div>
                      
                      <button
                        onClick={() => setFormData({ ...formData, player_count: Math.min(MAX_PLAYERS, formData.player_count + 1) })}
                        className="p-2 rounded-xl border border-gray-300 hover:bg-gray-50 transition-colors"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                    <p className="text-sm text-gray-500 mt-2">
                      {t('createBooking.maxPlayers')} ({MAX_PLAYERS})
                    </p>
                  </div>

                  {/* 特殊要求 - 两种模式都需要 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
{t('createBooking.specialRequests')}
                    </label>
                    <textarea
                      value={formData.special_requests}
                      onChange={(e) => setFormData({ ...formData, special_requests: e.target.value })}
                      placeholder={t('createBooking.specialRequestsPlaceholder')}
                      rows={3}
                      className="input resize-none"
                    />
                  </div>
                </div>
              </motion.div>
            )}

            {/* 步骤2：选择时段 */}
            {currentStep === 'timeslot' && (
              <motion.div
                key="timeslot"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="card card-comfortable"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('createBooking.selectTimeSlot')}</h3>
                
                {/* 日期和场地筛选 */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
{t('createBooking.selectDate')}
                    </label>
                    <input
                      type="date"
                      value={selectedDate}
                      onChange={(e) => {
                        setSelectedDate(e.target.value);
                        setFormData({ ...formData, date: e.target.value });
                      }}
                      min={getNzDateString()}
                      className="input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
{t('createBooking.venue')}
                    </label>
                    <select
                      value={selectedLocationId ?? ''}
                      onChange={(e) => {
                        const value = e.target.value ? Number(e.target.value) : undefined;
                        setSelectedLocationId(value);
                        setSelectedCourtId(undefined);
                      }}
                      className="input"
                    >
                      <option value="">{t('createBooking.allCourts')}</option>
                      {locations.map((location) => (
                        <option key={location.id} value={location.id}>
                          {location.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
{t('createBooking.courtFilter')}
                    </label>
                    <select
                      value={selectedCourtId ?? ''}
                      onChange={(e) => setSelectedCourtId(e.target.value ? Number(e.target.value) : undefined)}
                      className="input"
                    >
                      <option value="">{t('createBooking.allCourts')}</option>
                      {courts.map((court) => (
                        <option key={court.id} value={court.id}>
                          {court.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* 可用时段列表 */}
                <div className="space-y-3">
                  {slotsLoading ? (
                    <div className="text-center py-8">
                      <Clock className="h-12 w-12 text-gray-300 mx-auto mb-4 animate-spin" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">{t('common.loading')}</h3>
                      <p className="text-gray-500">{t('createBooking.noSlotsMessage')}</p>
                    </div>
                  ) : slotsError ? (
                    <div className="text-center py-8">
                      <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">{t('errors.generic')}</h3>
                      <p className="text-gray-500">{slotsError}</p>
                    </div>
                  ) : availableTimeSlots.length > 0 ? (
                    availableTimeSlots.map((slot) => (
                      <motion.button
                        key={slot.id}
                        onClick={() => {
                          setSelectedTimeSlot(slot);
                          setFormData({ ...formData, court_time_slot_id: slot.id, date: slot.date });
                        }}
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                        className={clsx(
                          "w-full p-4 rounded-xl border text-left transition-all hover:shadow-medium",
                          selectedTimeSlot?.id === slot.id
                            ? "border-primary-500 bg-primary-50"
                            : "border-gray-200 bg-white hover:border-gray-300"
                        )}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2">
                              <Clock className="h-4 w-4 text-gray-400" />
                              <span className="font-medium text-gray-900">
                                {slot.start_time} - {slot.end_time}
                              </span>
                            </div>
                            
                            <div className="flex items-center gap-2">
                              <MapPin className="h-4 w-4 text-gray-400" />
                              <span className="text-sm text-gray-600">{slot.court_name}</span>
                            </div>
                          </div>
                          
                          <div className="text-right">
                            <div className="text-lg font-bold text-gray-900">
                              {formatNZD(slot.base_price)}
                            </div>
                            <div className="text-sm text-gray-500">
{t('createBooking.remainingSpots', { count: slot.available_count })}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-500">
                            {slot.location_name}
                          </span>
                          
                          <div className="flex items-center gap-2">
                            {slot.available_count <= 2 && (
                              <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded-full">
{t('createBooking.almostFull')}
                              </span>
                            )}
                            
                            {selectedTimeSlot?.id === slot.id && (
                              <CheckCircle className="h-4 w-4 text-primary-500" />
                            )}
                          </div>
                        </div>
                      </motion.button>
                    ))
                  ) : (
                    <div className="text-center py-8">
                      <Clock className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">{t('createBooking.noAvailableSlots')}</h3>
                      <p className="text-gray-500">
                        {t('createBooking.noSlotsMessage')}
                      </p>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* 步骤3：支付方式 */}
            {currentStep === 'payment' && (
              <motion.div
                key="payment"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="card card-comfortable"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('createBooking.paymentMethod')}</h3>
                
                <div className="space-y-4">
                  {paymentOptions.map((method) => (
                    <motion.button
                      key={method.key}
                      onClick={() => setFormData({ ...formData, payment_method: method.key })}
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                      className={clsx(
                        "w-full p-4 rounded-xl border text-left transition-all hover:shadow-medium",
                        formData.payment_method === method.key
                          ? "border-primary-500 bg-primary-50"
                          : "border-gray-200 bg-white hover:border-gray-300"
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={clsx(
                            "p-3 rounded-xl",
                            formData.payment_method === method.key
                              ? "bg-primary-500 text-white"
                              : "bg-gray-100 text-gray-600"
                          )}>
                            <method.icon className="h-5 w-5" />
                          </div>
                          
                          <div>
                            <div className="font-medium text-gray-900">{method.label}</div>
                            <div className="text-sm text-gray-500">{method.description}</div>
                          </div>
                        </div>
                        
                        {formData.payment_method === method.key && (
                          <CheckCircle className="h-5 w-5 text-primary-500" />
                        )}
                      </div>
                    </motion.button>
                  ))}
                </div>
              </motion.div>
            )}

            {/* 步骤4：确认预订 */}
            {currentStep === 'confirm' && (
              <motion.div
                key="confirm"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="card card-comfortable"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('createBooking.confirmBookingInfo')}</h3>
                
                <div className="space-y-6">
                  {/* 客户信息 */}
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <h4 className="font-medium text-gray-900 mb-3">{t('createBooking.customerInfo')}</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-gray-500">{t('createBooking.name')}：</span>
                        <span className="font-medium">{formData.customer_name}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">{t('createBooking.phone')}：</span>
                        <span className="font-medium">{formData.customer_phone}</span>
                      </div>
                      {formData.customer_email && (
                        <div>
                          <span className="text-gray-500">{t('createBooking.email')}：</span>
                          <span className="font-medium">{formData.customer_email}</span>
                        </div>
                      )}
                      <div>
                        <span className="text-gray-500">{t('createBooking.playerCountLabel')}：</span>
                        <span className="font-medium">{formData.player_count} {t('createBooking.people')}</span>
                      </div>
                    </div>
                  </div>

                  {/* 预订信息 */}
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <h4 className="font-medium text-gray-900 mb-3">{t('createBooking.bookingInfo')}</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-gray-500">{t('createBooking.date')}：</span>
                        <span className="font-medium">{selectedTimeSlot?.date}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">{t('createBooking.time')}：</span>
                        <span className="font-medium">
                          {selectedTimeSlot?.start_time} - {selectedTimeSlot?.end_time}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">{t('createBooking.court')}：</span>
                        <span className="font-medium">{selectedTimeSlot?.court_name}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">{t('createBooking.venue')}：</span>
                        <span className="font-medium">{selectedTimeSlot?.location_name}</span>
                      </div>
                    </div>
                  </div>

                  {/* 支付信息 */}
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <h4 className="font-medium text-gray-900 mb-3">{t('createBooking.paymentInfo')}</h4>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500">{t('createBooking.paymentMethodLabel')}：</span>
                      <span className="font-medium">
                        {formData.payment_method === 'cash' ? t('createBooking.cashPayment') :
                         formData.payment_method === 'card' ? t('createBooking.cardPayment') : t('createBooking.transferPayment')}
                      </span>
                    </div>
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-200">
                      <span className="text-lg font-semibold text-gray-900">{t('createBooking.totalAmountLabel')}：</span>
                      <span className="text-2xl font-bold text-primary-600">
                        {formatNZD(calculateTotal())}
                      </span>
                    </div>
                  </div>

                  {/* 特殊要求 */}
                  {formData.special_requests && (
                    <div className="p-4 bg-primary-50 rounded-xl">
                      <h4 className="font-medium text-primary-900 mb-2">{t('createBooking.specialRequestsTitle')}</h4>
                      <p className="text-sm text-primary-800">{formData.special_requests}</p>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* 侧边栏摘要 */}
        <div className="lg:col-span-1">
          <div className="card card-comfortable sticky top-24">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('createBooking.bookingSummary')}</h3>
            
            <div className="space-y-4">
              {/* 客户信息摘要 */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <User className="h-4 w-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-700">{t('createBooking.customerInfoSummary')}</span>
                </div>
{formData.customer_name ? (
                  <div className="text-sm space-y-1">
                    <div className="font-medium">{formData.customer_name}</div>
                    {formData.customer_phone && (
                      <div className="text-gray-500">{formData.customer_phone}</div>
                    )}
                    <div className="text-gray-500">{formData.player_count} {t('createBooking.people')}</div>
                    {selectedCustomer && (selectedCustomer.member_level || selectedCustomer.total_bookings !== undefined) && (
                      <div className="flex items-center gap-1 mt-2">
                        <div className="w-2 h-2 bg-success-500 rounded-full"></div>
                        <span className="text-xs text-success-600 font-medium">
                          {selectedCustomer.member_level || t('common.unknownUser')}
                          {selectedCustomer.total_bookings !== undefined && (
                            <> | {selectedCustomer.total_bookings}{t('createBooking.times')}{t('createBooking.bookingHistory')}</>
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-sm text-gray-400">{t('createBooking.pendingFill')}</div>
                )}
              </div>

              {/* 时段信息摘要 */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="h-4 w-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-700">{t('createBooking.timeSlotSummary')}</span>
                </div>
                {selectedTimeSlot ? (
                  <div className="text-sm">
                    <div className="font-medium">
                      {selectedTimeSlot.start_time} - {selectedTimeSlot.end_time}
                    </div>
                    <div className="text-gray-500">{selectedTimeSlot.court_name}</div>
                    <div className="text-gray-500">{selectedTimeSlot.date}</div>
                  </div>
                ) : (
                  <div className="text-sm text-gray-400">{t('createBooking.pendingSelect')}</div>
                )}
              </div>

              {/* 金额摘要 */}
              <div className="p-3 bg-primary-50 rounded-lg border border-primary-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-primary-700">{t('createBooking.totalAmountSummary')}</span>
                  <DollarSign className="h-4 w-4 text-primary-600" />
                </div>
                <div className="text-2xl font-bold text-primary-600">
                  {formatNZD(calculateTotal())}
                </div>
                {selectedTimeSlot && (
                  <div className="text-xs text-primary-600 mt-1">
                    {formatNZD(selectedTimeSlot.base_price)} {t('createBooking.perHour')}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 底部操作按钮 */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between p-4 sm:p-6 bg-white border-t border-gray-200 sticky bottom-0 gap-3 sm:gap-0">
        <button
          onClick={handleBack}
          className="btn btn-secondary touch-manipulation order-2 sm:order-1"
        >
          {currentStep === 'customer' ? t('createBooking.backToList') : t('createBooking.previousStep')}
        </button>
        
        {currentStep === 'confirm' ? (
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !isStepValid()}
            className="btn btn-primary min-w-32 touch-manipulation order-1 sm:order-2"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
{t('createBooking.creating')}
              </>
            ) : (
              t('createBooking.confirmCreate')
            )}
          </button>
        ) : (
          <button
            onClick={handleNext}
            disabled={!isStepValid()}
            className="btn btn-primary touch-manipulation order-1 sm:order-2"
          >
{t('createBooking.nextStep')}
          </button>
        )}
      </div>
    </div>
  );
};

export default CreateBooking;
